<template>
  <div>
    <ExperimentComponent experiment-key="rvz28661SimplifiedRtbFlow">
      <template #control>
        <div
          ref="dateRangeEl"
          :class="computedClasses"
        >
          <button
            :disabled="disabled"
            type="button"
           
            class="text-gray-500 px-4 py-2 text-left cursor-pointer"
            :class="{ 'pl-0 border-r border-gray-200': flush }"
            @click="openCalendar('start')"
          >
            <span class="block text-sm">{{ t('checkIn') }}</span>
            <!-- Look into better formatting maybe use formatAsShortDateYear? -->
            <span
              class="font-semibold"
              :class="{ 'text-primary-950': computedStartDate }"
            >{{ computedStartDate || t('addDate') }}</span>
          </button>

          <button
            :disabled="disabled"
            type="button"
           
            class="text-gray-500 px-4 py-2 text-left cursor-pointer"
            :class="{ 'pr-0': flush }"
            @click="openCalendar('end')"
          >
            <span class="block text-sm">{{ t('checkOut') }}</span>
            <span
              class="font-semibold"
              :class="{ 'text-primary-950': computedEndDate }"
            >{{ computedEndDate || t('addDate') }}</span>
          </button>
        </div>

        <ClientOnly v-if="!disabled">
          <TFloatingBox
            v-if="isLgBreakpoint"
            v-model:visible="showForm"
            :trigger-el="dateRangeEl"
            :distance="16"
           
            class="z-60"
          >
            <LazySearchFormFieldDates
              :dates="{ start: bookingEstimateParams.dateStart, end: bookingEstimateParams.dateEnd }"
              :timezone="rvComputedTimezone"
              :disabled-dates="disabledDates"
              :unavailable-days="rvUnavailableDays"
              :min-date="rvMinBookableDate"
              :max-date="rvMaxBookableDate"
              :show-clear-button="!preventClearDates"
              @update:dates="updateDates"
            />
          </TFloatingBox>

          <TModalCritical
            v-else
            v-model:visible="showForm"
            :title="t('dates.title')"
            :no-teleport="noTeleport"
            hide-header
            size="2xl"
            :label-primary="t('actions.apply')"
            :label-tertiary="!preventClearDates ? t('clearDates') : undefined"
            @action:primary="(dismiss) => dismiss()"
            @action:tertiary="(dismiss) => handleActionTertiary(dismiss)"
            @close="handleDatesCompleted"
          >
            <LazySearchFormFieldDates
              :dates="{ start: bookingEstimateParams.dateStart, end: bookingEstimateParams.dateEnd }"
              :timezone="rvComputedTimezone"
              :disabled-dates="disabledDates"
              :unavailable-days="rvUnavailableDays"
              :min-date="rvMinBookableDate"
              :max-date="rvMaxBookableDate"
              :one-column="!isSmBreakpoint"
              @update:dates="updateDates"
            />
          </TModalCritical>

          <!-- Prevent flickering on page load -->
          <template #fallback />
        </ClientOnly>
      </template>
      <template #variant>
        <MoleculeDrillDown
          ref="dateRangeDrillDownEl"
          :opened="showForm && !isLgBreakpoint && !disabled"
          :title="t('dates.title')"
          :value="computedDatesLabel"
          :placeholder="t('addDate')"
          :disabled="disabled || hideEditButton"
          :badge="hasIncompleteDateRange ? t('required') : undefined"
          test-id="change-start-date"
          @open="openCalendar()"
          @close="closeCalendar"
        >
          <LazySearchFormFieldDates
            :dates="{ start: bookingEstimateParams.dateStart, end: bookingEstimateParams.dateEnd }"
            :timezone="rvComputedTimezone"
            :disabled-dates="disabledDates"
            :unavailable-days="rvUnavailableDays"
            :min-date="rvMinBookableDate"
            :max-date="rvMaxBookableDate"
            :one-column="!isSmBreakpoint"
            @update:dates="updateDates"
          />

          <div class="mt-auto flex justify-between">
            <TButton
              v-if="!preventClearDates"
              variant="tertiary"
              @click="updateDates({ start: undefined, end: undefined })"
            >
              {{ t('clearDates') }}
            </TButton>

            <TButton
              class="ml-auto"
              @click="closeCalendar"
            >
              {{ t('actions.apply') }}
            </TButton>
          </div>
        </MoleculeDrillDown>

        <ClientOnly v-if="!disabled">
          <TFloatingBox
            v-if="isLgBreakpoint"
            v-model:visible="showForm"
            :trigger-el="dateRangeDrillDownEl?.$el"
            :distance="16"
           
            class="z-60"
          >
            <LazySearchFormFieldDates
              :dates="{ start: bookingEstimateParams.dateStart, end: bookingEstimateParams.dateEnd }"
              :timezone="rvComputedTimezone"
              :disabled-dates="disabledDates"
              :unavailable-days="rvUnavailableDays"
              :min-date="rvMinBookableDate"
              :max-date="rvMaxBookableDate"
              :show-clear-button="!preventClearDates"
              @update:dates="updateDates"
            />
          </TFloatingBox>

          <!-- Prevent flickering on page load -->
          <template #fallback />
        </ClientOnly>
      </template>
    </ExperimentComponent>
  </div>
</template>

<script setup lang="ts">
import type { BlockedRange } from '~/types'

import type { SearchDates } from '~/types/search'

const props = defineProps<{
  noTeleport?: boolean
  disabled?: boolean
  startDate?: string
  endDate?: string
  disabledDates?: BlockedRange[]
  flush?: boolean
  hideEditButton?: boolean
  preventClearDates?: boolean
}>()

const emit = defineEmits<{
  update: [dates: SearchDates]
  completed: []
}>()

const { t } = useI18n()

const { isLgBreakpoint, isSmBreakpoint } = useBreakpoint()
const { formatAsShortDateYear, formatAsShortDateRangeWithYear } = useDates()

const dateRangeEl = useTemplateRef('dateRangeEl')
const dateRangeDrillDownEl = useTemplateRef('dateRangeDrillDownEl')

const computedClasses = computed(() => [
  'grid grid-cols-2 border border-gray-200',
  {
    'border-l-0 border-r-0': props.flush,
    'rounded-sm': !props.flush,
  },
])

const showForm = ref(false)
function openCalendar(_rangePosition?: string) {
  showForm.value = true
}

function closeCalendar() {
  showForm.value = false
  handleDatesCompleted()
}

const { bookingEstimateParams, hasIncompleteDateRange, updateDateRange } = useBookingEstimate()

const computedStartDate = computed(() => props.startDate || bookingEstimateParams.value.dateStart)
const computedEndDate = computed(() => props.endDate || bookingEstimateParams.value.dateEnd)

const hasDates = computed(() => Boolean(computedStartDate.value) && Boolean(computedEndDate.value))

const computedDatesLabel = computed(() => {
  if (!computedStartDate.value) {
    return
  }

  return computedEndDate.value
    ? formatAsShortDateRangeWithYear(computedStartDate.value, computedEndDate.value)
    : formatAsShortDateYear(computedStartDate.value)
})

function updateDates(newDates: SearchDates) {
  updateDateRange({
    dateStart: newDates.start || null,
    dateEnd: newDates.end || null,
  })

  // Close the form if both dates are selected
  // Temporary disabling auto-close for small screens because the overlay drawer is buggy
  if (newDates.start && newDates.end && isLgBreakpoint.value) {
    showForm.value = false
    handleDatesCompleted()
  }

  emit('update', newDates)
}

function handleDatesCompleted() {
  if (hasDates.value) {
    emit('completed')
  }
}

defineExpose({
  openCalendar,
})

const { rvMinBookableDate, rvMaxBookableDate, rvUnavailableDays, rvComputedTimezone } = useRvDetails()

function handleActionTertiary(dismiss: () => void) {
  updateDates({ start: undefined, end: undefined })

  dismiss()
}
</script>

<i18n src="~~/i18n/locales/common/search/form/dates.json" lang="json" />

<i18n lang="json">
{
  "en": {
    "addDate": "Add dates",
    "checkIn": "Check in",
    "checkOut": "Check out",
    "clearDates": "Clear dates"
  },
  "fr": {
    "addDate": "Ajouter des dates",
    "checkIn": "Date de départ",
    "checkOut": "Date de retour",
    "clearDates": "Réinitialiser les dates"
  }
}
</i18n>
